/**
 * Timestamp twin of the Python skeleton producer's datetime handling.
 *
 * `Event.timestamp` is a `UtcDatetime`: every parsed timestamp is coerced to
 * UTC (offset-less strings are treated as UTC), and the producer emits
 * `datetime.isoformat()` of the UTC datetime — no fraction when microseconds
 * are zero, otherwise six digits, offset always `+00:00`. The producer also
 * compares timestamps at microsecond precision, beyond JS `Date`, so we
 * carry a microsecond epoch.
 */

export interface PyTimestamp {
  /** Microseconds since the Unix epoch (instant, for comparisons). */
  epochUs: number;
  /** Python `datetime.isoformat()` rendering of the UTC datetime. */
  iso: string;
}

const TS_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6})\d*)?(Z|[+-]\d{2}:?\d{2})?$/;

/** Parse an ISO-8601 timestamp; offset-less values are treated as UTC. */
export const parsePyTimestamp = (value: string): PyTimestamp => {
  const m = TS_RE.exec(value);
  if (!m) {
    throw new Error(`Unparseable timestamp: ${value}`);
  }
  const [, year, month, day, hour, minute, second, fraction, offset] = m;
  const micros = fraction ? Number(fraction.padEnd(6, "0")) : 0;
  const baseMs = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );
  let offsetMinutes = 0;
  if (offset && offset !== "Z") {
    const sign = offset[0] === "-" ? -1 : 1;
    const digits = offset.slice(1).replace(":", "");
    offsetMinutes =
      sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2)));
  }
  const epochUs = (baseMs - offsetMinutes * 60_000) * 1000 + micros;
  return { epochUs, iso: formatPyTimestamp(epochUs) };
};

/** Python `datetime.isoformat()` of a UTC microsecond epoch. */
export const formatPyTimestamp = (epochUs: number): string => {
  const micros = ((epochUs % 1_000_000) + 1_000_000) % 1_000_000;
  const date = new Date((epochUs - micros) / 1000);
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  const fraction = micros === 0 ? "" : `.${pad(micros, 6)}`;
  return (
    `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}` +
    `${fraction}+00:00`
  );
};

/** Later of two timestamps (Python `max` — ties keep the first argument). */
export const maxPyTimestamp = (a: PyTimestamp, b: PyTimestamp): PyTimestamp =>
  b.epochUs > a.epochUs ? b : a;
